import { useState, useEffect } from 'react';
import { collection, doc, onSnapshot, query, orderBy, limit, where } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Timestamp } from 'firebase/firestore';

// UTILMD specific interfaces
interface UTILMDProvider {
  name: string;
  description: string;
  status: 'active' | 'checked' | 'no_access' | 'error' | 'pending';
  connectionStatus: string;
  directoriesChecked: number;
  utilmdFiles: number;
  c01Requests: number;
  timestamp: string;
  files?: string[];
  c01RequestFiles?: string[];
  error?: string;
  errorType?: string;
  message?: string;
}

interface UTILMDSummary {
  totalProviders: number;
  activeProviders: number;
  noAccessProviders: number;
  totalUtilmdFiles: number;
  totalC01Requests: number;
  connectionErrors: number;
}

interface UTILMDData {
  timestamp: string;
  lastCheck: string;
  summary: UTILMDSummary;
  providers: Record<string, UTILMDProvider>;
}

interface C01Request {
  id: string;
  provider: string;
  fileName: string;
  filePath: string;
  timestamp: string;
  status: 'pending' | 'processing' | 'processed' | 'error';
  processed: boolean;
  fileSize: number;
  analysis?: any;
  processingResult?: any;
}

interface Activity {
  type: 'connection' | 'error' | 'c01_request';
  provider: string;
  message: string;
  timestamp: string;
  status: 'success' | 'error' | 'pending' | 'processed';
}

// Legacy interfaces for backward compatibility
interface ProcessingStatus {
  status: 'idle' | 'running' | 'completed' | 'error';
  started_at?: string;
  completed_at?: string;
  total_files: number;
  processed_files: number;
  success_count: number;
  error_count: number;
  current_provider?: string;
  last_error?: string;
  updated_at: Timestamp;
}

interface ValidationResult {
  id: string;
  file_name: string;
  message_type: string;
  valid: boolean;
  timestamp: string;
  provider: string;
  errors: string[];
  warnings: string[];
  partner_count: number;
  pod_count: number;
  processing_time_ms: number;
  created_at: Timestamp;
}

interface UTILMDStatistics {
  date: string;
  total_files: number;
  success_count: number;
  error_count: number;
  total_partners: number;
  total_pods: number;
  avg_processing_time_ms: number;
  updated_at: Timestamp;
}

interface ProviderStatistics {
  date: string;
  provider: string;
  total_files: number;
  success_count: number;
  error_count: number;
  avg_processing_time_ms: number;
  updated_at: Timestamp;
}

interface MessageTypeStatistics {
  date: string;
  message_type: string;
  total_files: number;
  success_count: number;
  error_count: number;
  avg_processing_time_ms: number;
  updated_at: Timestamp;
}

// Main UTILMD hook
export const useUTILMD = () => {
  const [utilmdData, setUtilmdData] = useState<UTILMDData | null>(null);
  const [c01Requests, setC01Requests] = useState<C01Request[]>([]);
  const [processingStatus, setProcessingStatus] = useState<ProcessingStatus | null>(null);
  const [recentResults, setRecentResults] = useState<ValidationResult[]>([]);
  const [dailyStats, setDailyStats] = useState<UTILMDStatistics | null>(null);
  const [providerStats, setProviderStats] = useState<Record<string, ProviderStatistics>>({});
  const [messageTypeStats, setMessageTypeStats] = useState<Record<string, MessageTypeStatistics>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // UTILMD monitoring adatok real-time figyelése
    const unsubscribeStatus = onSnapshot(
      doc(db, 'utilmd_monitoring', 'current_status'),
      (doc) => {
        if (doc.exists()) {
          const data = doc.data() as UTILMDData;
          setUtilmdData(data);
          setError(null);
        } else {
          setError('Nincs UTILMD monitoring adat');
        }
        setLoading(false);
      },
      (err) => {
        console.error('UTILMD monitoring hiba:', err);
        setError('Hiba az UTILMD adatok betöltésekor');
        setLoading(false);
      }
    );

    // C01 kérelmek real-time figyelése
    const unsubscribeC01 = onSnapshot(
      query(
        collection(db, 'utilmd_c01_requests'),
        orderBy('timestamp', 'desc'),
        limit(50)
      ),
      (snapshot) => {
        const requests: C01Request[] = [];
        snapshot.forEach((doc) => {
          requests.push({
            id: doc.id,
            ...doc.data()
          } as C01Request);
        });
        setC01Requests(requests);
      },
      (err) => {
        console.error('C01 kérelmek betöltési hiba:', err);
      }
    );

    // Processing status listener
    const unsubscribeProcessing = onSnapshot(
      doc(db, 'utilmd_processing', 'current_status'),
      (doc) => {
        if (doc.exists()) {
          setProcessingStatus(doc.data() as ProcessingStatus);
        }
      }
    );

    // Recent results listener
    const unsubscribeResults = onSnapshot(
      query(
        collection(db, 'utilmd_validation_results'),
        orderBy('created_at', 'desc'),
        limit(20)
      ),
      (snapshot) => {
        const results: ValidationResult[] = [];
        snapshot.forEach((doc) => {
          results.push({ id: doc.id, ...doc.data() } as ValidationResult);
        });
        setRecentResults(results);
      }
    );

    // Daily statistics listener
    const today = new Date().toISOString().split('T')[0];
    const unsubscribeDaily = onSnapshot(
      doc(db, 'utilmd_statistics', today),
      (doc) => {
        if (doc.exists()) {
          setDailyStats(doc.data() as UTILMDStatistics);
        }
      }
    );

    return () => {
      unsubscribeStatus();
      unsubscribeC01();
      unsubscribeProcessing();
      unsubscribeResults();
      unsubscribeDaily();
    };
  }, []);

  const refreshData = async () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  const getPendingC01Requests = () => {
    return c01Requests.filter(req => !req.processed);
  };

  const getProcessedC01Requests = () => {
    return c01Requests.filter(req => req.processed);
  };

  const getProviderStats = () => {
    if (!utilmdData) return null;

    return {
      totalProviders: utilmdData.summary.totalProviders,
      activeProviders: utilmdData.summary.activeProviders,
      connectedProviders: Object.values(utilmdData.providers).filter(
        p => p.connectionStatus === 'connected'
      ).length,
      providersWithFiles: Object.values(utilmdData.providers).filter(
        p => p.utilmdFiles > 0
      ).length,
      providersWithC01: Object.values(utilmdData.providers).filter(
        p => p.c01Requests > 0
      ).length
    };
  };

  const getRecentActivity = (): Activity[] => {
    if (!utilmdData) return [];

    const activities: Activity[] = [];
    
    // Provider kapcsolódási státuszok
    Object.entries(utilmdData.providers).forEach(([name, provider]) => {
      if (provider.connectionStatus === 'connected') {
        activities.push({
          type: 'connection',
          provider: name,
          message: `${name} sikeresen kapcsolódva`,
          timestamp: provider.timestamp,
          status: 'success'
        });
      } else if (provider.connectionStatus === 'error') {
        activities.push({
          type: 'error',
          provider: name,
          message: `${name} kapcsolódási hiba: ${provider.error}`,
          timestamp: provider.timestamp,
          status: 'error'
        });
      }
    });

    // C01 kérelmek
    c01Requests.slice(0, 10).forEach(request => {
      activities.push({
        type: 'c01_request',
        provider: request.provider,
        message: `Új C01 kérelem: ${request.fileName}`,
        timestamp: request.timestamp,
        status: request.processed ? 'processed' : 'pending'
      });
    });

    return activities.sort((a, b) => 
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    ).slice(0, 20);
  };

  // Computed values
  const isProcessing = processingStatus?.status === 'running';
  const hasError = processingStatus?.status === 'error' || !!error;
  const currentProgress = processingStatus ? 
    (processingStatus.processed_files / processingStatus.total_files * 100) : 0;
  const successRate = dailyStats && dailyStats.total_files > 0 ? 
    (dailyStats.success_count / dailyStats.total_files * 100) : 0;
  const recentErrors = recentResults.filter(r => !r.valid).map(r => r.errors).flat();
  const providerSuccessRates = Object.fromEntries(
    Object.entries(providerStats).map(([provider, stats]) => [
      provider,
      stats.total_files > 0 ? (stats.success_count / stats.total_files * 100) : 0
    ])
  );
  const messageTypeDistribution = Object.fromEntries(
    Object.entries(messageTypeStats).map(([type, stats]) => [
      type,
      stats.total_files
    ])
  );

  return {
    // New UTILMD data
    utilmdData,
    c01Requests,
    refreshData,
    getPendingC01Requests,
    getProcessedC01Requests,
    getProviderStats,
    getRecentActivity,
    
    // Status and processing
    processingStatus,
    isProcessing,
    hasError,
    loading,
    error,
    currentProgress,
    
    // Results and statistics
    recentResults,
    dailyStats,
    successRate,
    providerStats,
    providerSuccessRates,
    messageTypeStats,
    messageTypeDistribution,
    recentErrors,
    
    // Computed metrics for backward compatibility
    todayProcessed: dailyStats?.total_files || utilmdData?.summary.totalUtilmdFiles || 0,
    todaySuccess: dailyStats?.success_count || utilmdData?.summary.totalUtilmdFiles || 0,
    todayErrors: dailyStats?.error_count || utilmdData?.summary.connectionErrors || 0,
    avgProcessingTime: dailyStats?.avg_processing_time_ms || 0,
    totalPartners: dailyStats?.total_partners || utilmdData?.summary.totalProviders || 0,
    totalPods: dailyStats?.total_pods || 0
  };
};

// Hook for historical data
export const useUTILMDHistory = (days: number = 7) => {
  const [historicalStats, setHistoricalStats] = useState<UTILMDStatistics[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const dates: string[] = [];
    for (let i = 0; i < days; i++) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      dates.push(date.toISOString().split('T')[0]);
    }

    const fetchHistoricalData = async () => {
      try {
        const stats: UTILMDStatistics[] = [];
        
        for (const date of dates) {
          const unsubscribe = onSnapshot(
            doc(db, 'utilmd_statistics', date),
            (doc) => {
              if (doc.exists()) {
                stats.push({ ...doc.data(), date } as UTILMDStatistics);
              }
            }
          );
        }
        
        setHistoricalStats(stats.sort((a, b) => a.date.localeCompare(b.date)));
        setLoading(false);
      } catch (error) {
        console.error('Error fetching historical data:', error);
        setLoading(false);
      }
    };

    fetchHistoricalData();
  }, [days]);

  return {
    historicalStats,
    loading
  };
};

// Hook for specific provider data
export const useProviderUTILMD = (provider: string) => {
  const [providerData, setProviderData] = useState<ProviderStatistics | null>(null);
  const [providerResults, setProviderResults] = useState<ValidationResult[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    
    // Provider statistics
    const unsubscribeStats = onSnapshot(
      doc(db, 'utilmd_provider_stats', `${today}_${provider}`),
      (doc) => {
        if (doc.exists()) {
          setProviderData(doc.data() as ProviderStatistics);
        } else {
          setProviderData(null);
        }
        setLoading(false);
      }
    );

    // Provider validation results
    const q = query(
      collection(db, 'utilmd_validation_results'),
      where('provider', '==', provider),
      orderBy('created_at', 'desc'),
      limit(20)
    );

    const unsubscribeResults = onSnapshot(
      q,
      (snapshot) => {
        const results: ValidationResult[] = [];
        snapshot.forEach((doc) => {
          results.push({ id: doc.id, ...doc.data() } as ValidationResult);
        });
        setProviderResults(results);
      }
    );

    return () => {
      unsubscribeStats();
      unsubscribeResults();
    };
  }, [provider]);

  return {
    providerData,
    providerResults,
    loading,
    successRate: providerData && providerData.total_files > 0 ? 
      (providerData.success_count / providerData.total_files * 100) : 0,
    errorRate: providerData && providerData.total_files > 0 ? 
      (providerData.error_count / providerData.total_files * 100) : 0
  };
}; 